/* eslint-disable no-plusplus */
import { Button } from '@components/common';
import { useAppSelector } from '@hooks/useRedux';
import { selectOutletId } from '@store/auth/authSelectors';
import {
  AlertCircle,
  Shield,
  ShieldAlert,
  ToggleLeft,
  ToggleRight,
  Trash2,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDeviceMutations, useDevices } from '../../../hooks/useDevices';
import { apiError, apiSuccess } from '../../../util/notify';

import { getOSMetadata } from '../../../lib/utils';

export const RegisteredDevicesPage: React.FC = () => {
  const selectedOutletId = useAppSelector(selectOutletId);
  const { data: devicesData, isLoading, error } = useDevices(selectedOutletId);
  const { toggleMutation, deleteMutation } = useDeviceMutations();

  const devices = useMemo(() => devicesData || [], [devicesData]);

  // Filter and pagination states
  const [selectedOS, setSelectedOS] = useState<string>('All');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  // Aggregate OS counts for metrics cards
  const metrics = useMemo(() => {
    let total = 0;
    let enabledCount = 0;
    const osMap: Record<string, number> = {
      Windows: 0,
      macOS: 0,
      Linux: 0,
      iOS: 0,
      Android: 0,
      Other: 0,
    };

    devices.forEach((dev) => {
      total++;
      if (dev.is_enabled) {
        enabledCount++;
      }
      const osMeta = getOSMetadata(dev.operating_system);
      osMap[osMeta.name] = (osMap[osMeta.name] || 0) + 1;
    });

    return { total, enabledCount, osMap };
  }, [devices]);

  // Filtered devices list based on selection
  const filteredDevices = useMemo(() => {
    return devices.filter((dev) => {
      if (selectedOS === 'All') return true;
      const osMeta = getOSMetadata(dev.operating_system);
      return osMeta.name === selectedOS;
    });
  }, [devices, selectedOS]);

  // Paginated chunk
  const paginatedDevices = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredDevices.slice(start, start + itemsPerPage);
  }, [filteredDevices, currentPage]);

  const totalPages = Math.ceil(filteredDevices.length / itemsPerPage) || 1;

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedOS]);

  // Toggle allowed/disallowed status
  const handleToggleEnabled = async (
    deviceId: string,
    isCurrentlyEnabled: boolean,
  ) => {
    try {
      await toggleMutation.mutateAsync({
        deviceId,
        isEnabled: !isCurrentlyEnabled,
      });
      apiSuccess('Device status updated successfully');
    } catch (err) {
      apiError(
        err instanceof Error ? err.message : 'Failed to update device status',
      );
    }
  };

  // Permanently unregister device
  const handleDelete = async (deviceId: string, deviceName: string) => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        `Are you sure you want to remove ${deviceName}? This device will immediately lose fast passcode authentication access.`,
      )
    ) {
      return;
    }

    try {
      await deleteMutation.mutateAsync(deviceId);
      apiSuccess('Device removed successfully');
    } catch (err) {
      apiError(err instanceof Error ? err.message : 'Failed to remove device');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-black">Registered Devices</h2>
        <p className="mt-1 text-sm text-gray-600">
          Manage authorized client terminals and tablets allowed to use fast
          passcode logins.
        </p>
      </div>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 font-medium">
          {error.message}
        </div>
      )}

      {/* Aggregate Counts Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Total Terminals
          </p>
          <p className="mt-2 text-3xl font-semibold text-black">
            {metrics.total}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Active/Enabled
          </p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-3xl font-semibold text-black">
              {metrics.enabledCount}
            </span>
            <span className="text-xs font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
              ONLINE ALLOWED
            </span>
          </div>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Windows / macOS
          </p>
          <p className="mt-2 text-xl font-bold text-gray-800">
            Win: {metrics.osMap.Windows} • Mac: {metrics.osMap.macOS}
          </p>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
            Mobile & Linux
          </p>
          <p className="mt-2 text-xl font-bold text-gray-800">
            iOS: {metrics.osMap.iOS} • Android: {metrics.osMap.Android} • Linux:{' '}
            {metrics.osMap.Linux}
          </p>
        </div>
      </div>

      {/* Filter and Content Card */}
      <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        {/* OS Filter Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-gray-200 bg-app-bg px-5 py-4">
          <div className="flex flex-wrap gap-2">
            {[
              'All',
              'Windows',
              'macOS',
              'Linux',
              'iOS',
              'Android',
              'Other',
            ].map((os) => {
              const isActive = selectedOS === os;
              return (
                <button
                  key={os}
                  type="button"
                  onClick={() => setSelectedOS(os)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
                    isActive
                      ? 'bg-emerald-700 text-white border-emerald-700 shadow-sm'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {os} ({os === 'All' ? devices.length : metrics.osMap[os] || 0}
                  )
                </button>
              );
            })}
          </div>
        </div>

        {/* Devices Table Content */}
        {isLoading && devices.length === 0 ? (
          <div className="flex items-center justify-center h-64">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-pos-primary" />
          </div>
        ) : filteredDevices.length === 0 ? (
          <div className="p-10 text-center text-gray-500">
            <AlertCircle className="mx-auto mb-3 text-gray-400" size={36} />
            <p className="font-semibold text-black">
              No registered devices found
            </p>
            <p className="text-xs text-gray-600 mt-1">
              Sign in on a device with an email and password to automatically
              register it for this outlet.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-5 py-3.5 text-left font-semibold">
                    Device
                  </th>
                  <th className="px-5 py-3.5 text-left font-semibold">
                    Operating System
                  </th>
                  <th className="px-5 py-3.5 text-left font-semibold">
                    Fingerprint / HW ID
                  </th>
                  <th className="px-5 py-3.5 text-left font-semibold">
                    Fast Access Status
                  </th>
                  <th className="px-5 py-3.5 text-left font-semibold">
                    Added Date
                  </th>
                  <th className="px-5 py-3.5 text-right font-semibold">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedDevices.map((device) => {
                  const osMeta = getOSMetadata(device.operating_system);
                  const IconComponent = osMeta.icon;

                  return (
                    <tr
                      key={device.id}
                      className="border-b border-gray-100 hover:bg-gray-50/50 last:border-b-0"
                    >
                      <td className="px-5 py-4 font-semibold text-black">
                        {device.device_name}
                      </td>
                      <td className="px-5 py-4 align-middle">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md border text-xs font-semibold ${osMeta.color}`}
                          >
                            <IconComponent size={14} />
                            {osMeta.name}
                          </span>
                          <span className="text-xs text-gray-500 truncate max-w-[120px]">
                            {device.operating_system
                              .replace(osMeta.name, '')
                              .trim()}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-4 text-xs font-mono text-gray-600">
                        {device.device_fingerprint}
                      </td>
                      <td className="px-5 py-4">
                        <button
                          type="button"
                          onClick={() =>
                            handleToggleEnabled(device.id, device.is_enabled)
                          }
                          className="flex items-center gap-2 text-xs font-semibold"
                        >
                          {device.is_enabled ? (
                            <>
                              <ToggleRight
                                size={32}
                                className="text-emerald-700 cursor-pointer"
                              />
                              <span className="inline-flex items-center gap-1 text-emerald-800 bg-emerald-50 border border-emerald-100 px-2 py-0.5 rounded text-[10px] font-bold">
                                <Shield size={10} />
                                ALLOWED
                              </span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft
                                size={32}
                                className="text-gray-400 cursor-pointer"
                              />
                              <span className="inline-flex items-center gap-1 text-amber-800 bg-amber-50 border border-amber-100 px-2 py-0.5 rounded text-[10px] font-bold">
                                <ShieldAlert size={10} />
                                BLOCKED
                              </span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-5 py-4 text-sm text-gray-600">
                        {new Date(device.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-5 py-4 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                          onClick={() =>
                            handleDelete(device.id, device.device_name)
                          }
                        >
                          <Trash2 size={14} />
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 px-5 py-4 bg-app-bg text-sm">
                <span className="text-xs text-gray-600">
                  Showing {(currentPage - 1) * itemsPerPage + 1} -{' '}
                  {Math.min(currentPage * itemsPerPage, filteredDevices.length)}{' '}
                  of {filteredDevices.length} devices
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => prev - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => prev + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
